import React, { useCallback, useState } from 'react';
import { ToolMode, DEFAULT_TOOL_MODES } from '../../constants/toolModes';
import { useAuth } from '../../contexts/AuthContext';
import { useAgent } from '../../contexts/AgentContext';
// import { useAuth } from "../../hooks/chat/useAuth";
import { useConversations } from './state/useConversations';
import { useMessages } from './state';
import { useStreamState } from './state';
import { storage } from '../../lib/storage';
import { notify } from '../../utils/toast';
import { Message, UsageMetadata, Conversation } from '../../types/chat';
import { handleStreamData } from './actions/streamHandlers';
import { getAvatarUrl } from '../../utils/avatar';


export const useChat = (
  onCreditUpdate?: (credit: number) => void,
  loadSharedConversation?: (conversationId: string) => Promise<boolean>,
  navigate?: (path: string, options?: { replace: boolean }) => void
) => {
  const [tools, setTools] = useState<ToolMode[]>(DEFAULT_TOOL_MODES);
  const { state: { user } } = useAuth();
  const { selectedAgents } = useAgent();
  const {
    messages,
    setMessages,
    loadMessages
  } = useMessages();

  const {
    state: { sending },
    stopStream,
    startStream
  } = useStreamState();

  // Create ref for selectConversation to avoid dependency cycles
  const selectConversationRef = React.useRef<((conversation: Conversation) => Promise<void>) | null>(null);

  const {
    conversations,
    setConversations,
    currentConversation,
    setCurrentConversation,
    hasNewChat,
    setHasNewChat,
    loading,
    setLoading,
    createConversation,
    deleteConversation,
    syncConversations
  } = useConversations(selectConversationRef);

  const togglePin = useCallback(async () => {
    console.log('togglePin called');
    if (!currentConversation || !user?.address) {
      console.log('Missing currentConversation or user address:', { currentConversation, userAddress: user?.address });
      return;
    }

    try {
      const walletAddress = localStorage.getItem('walletAddress');
      if (!walletAddress) {
        throw new Error('Wallet address not found');
      }

      console.log('Current conversation before update:', currentConversation);
      const updatedConversation = {
        ...currentConversation,
        isPinned: !currentConversation.isPinned
      };
      console.log('Updated conversation:', updatedConversation);

      // First update in storage to ensure persistence
      await storage.updateConversation(walletAddress, currentConversation.id, {
        isPinned: !currentConversation.isPinned
      });
      console.log('Storage updated successfully');

      // Then update the UI state
      setCurrentConversation(updatedConversation);
      setConversations(prev => {
        const updated = prev.map(conv =>
          conv.id === currentConversation.id ? updatedConversation : conv
        );
        console.log('Updated conversations list:', updated);
        return updated;
      });

      notify.success(updatedConversation.isPinned ? 'Conversation pinned' : 'Conversation unpinned');
    } catch (error) {
      console.error('Error toggling pin:', error);
      notify.error('Failed to update conversation');
    }
  }, [currentConversation, user?.address, setCurrentConversation, setConversations]);


  const selectConversation = useCallback(async (conversation: Conversation) => {
    if (conversation.isNew) return;

    try {
      setLoading(true);
      if (!user?.address) {
        throw new Error('Not connected to wallet');
      }
      if (!conversation.isSharedTo) {
        const success = await loadMessages(user.address, conversation.id);
        if (success) {
          setCurrentConversation(conversation);
          setHasNewChat(false);
        }
      } else {
        // For shared conversations, use the loadSharedConversation function
        if (loadSharedConversation && navigate) {
          // Clear the current conversation selection
          setCurrentConversation(null);
          
          // Navigate to the shared conversation URL without page reload
          navigate(`/convo/shared/${conversation.id}`, { replace: true });
          
          // Load the shared conversation messages
          await loadSharedConversation(conversation.id);
        } else {
          // Fallback to old behavior if functions aren't provided
          window.location.href = `/convo/shared/${conversation.id}`;
        }
      }


    } catch (error) {
      console.error('Error selecting conversation:', error);
      notify.error('Failed to load conversation messages');
    } finally {
      setLoading(false);
    }
  }, [user?.address, loadMessages, setCurrentConversation, setHasNewChat, setLoading]);

  // Update ref when selectConversation changes
  React.useEffect(() => {
    selectConversationRef.current = selectConversation;
  }, [selectConversation]);

  const handleNewChat = useCallback(async () => {
    setMessages([]);
    const newConversation = await createConversation();
    if (newConversation) {
      setCurrentConversation(newConversation);
      setHasNewChat(true);
    }
    return newConversation;
  }, [createConversation, setMessages, setCurrentConversation, setHasNewChat]);

  const sendMessage = useCallback(async (content: string, currentTools: ToolMode[]) => {
    if (!content.trim()) {
      notify.error('Please enter a message');
      return;
    }

    // Update the tools state with the current tools being used for this message
    setTools(currentTools);

    let auth: {
      jwt: string;
      walletAddress: string;
      nonce: string;
      did: string;
    };
    let activeConversation = currentConversation;
    let currentContent = '';
    let serverMessageId: string | null = null;
    let workflow_steps: Array<{ title: string; timestamp: string }> = [];
    let tempAssistantMessage: Message;

    try {
      if (!user?.address) {
        throw new Error('Not connected to wallet');
      }

      const profile = await storage.getProfile(user.address);
      if (!profile?.jwt || !profile?.nonce || !profile?.did) {
        throw new Error('Profile not found or invalid');
      }

      auth = {
        jwt: profile.jwt,
        walletAddress: user.address,
        nonce: profile.nonce,
        did: profile.did
      };

      if (!currentConversation) {
        const newConversation = await handleNewChat();
        if (!newConversation) {
          throw new Error('Failed to create a new conversation');
        }
        activeConversation = newConversation;
      }

      const avatarUrl = getAvatarUrl(profile);
      const userMessage: Message = {
        id: crypto.randomUUID(),
        content,
        sender: 'user',
        created_at: new Date().toISOString(),
        avatar: avatarUrl
      };

      tempAssistantMessage = {
        id: crypto.randomUUID(),
        content: '',
        sender: 'assistant',
        created_at: new Date().toISOString(),
        avatar: '/images/Agent.png',
        metadata: {
          workflow_info: {
            steps: workflow_steps
          }
        }
      };

      setMessages((prev: Message[]) => [...prev, userMessage, tempAssistantMessage]);

      const toolsToUse = currentTools || DEFAULT_TOOL_MODES;

      const success = await startStream({
        auth,
        content,
        conversationId: activeConversation?.isNew ? null : (activeConversation?.id || null),
        tools: toolsToUse,
        selectedAgents,
        onData: async (data) => {
          if (!activeConversation) {
            throw new Error('No active conversation');
          }

          const context = {
            auth,
            activeConversation,
            userMessage,
            tempAssistantMessage,
            messageState: {
              currentContent,
              serverMessageId,
              workflow_steps,
              updateMessageContent: (content: string, messageId?: string) => {
                currentContent = content;
                if (messageId) {
                  serverMessageId = messageId;
                }
              },
              finalizeMessage: (usage: UsageMetadata) => ({
                id: serverMessageId || tempAssistantMessage?.id,
                content: currentContent,
                sender: 'assistant' as const,
                created_at: new Date().toISOString(),
                metadata: {
                  usage,
                  workflow_info: {
                    steps: workflow_steps
                  }
                }
              })
            },
            setMessages,
            setConversations,
            setCurrentConversation,
            setHasNewChat,
            onCreditUpdate
          };

          activeConversation = await handleStreamData(data, context);
        }
      });

      if (!success) {
        setMessages((prev: Message[]) => prev.filter(msg => msg && tempAssistantMessage ? msg.id !== tempAssistantMessage.id : true));
        setHasNewChat(false);
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      const userMessage = {
        id: crypto.randomUUID(),
        content,
        sender: 'user' as const,
        created_at: new Date().toISOString(),
        error: true
      };
      setMessages((prev: Message[]) => [
        ...prev.filter(msg => msg && tempAssistantMessage ? msg.id !== tempAssistantMessage.id : true),
        userMessage
      ]);
      notify.error(error.message || 'Failed to send message');
      setHasNewChat(false);
    }
  }, [
    user,
    currentConversation,
    handleNewChat,
    startStream,
    setMessages,
    setConversations,
    setCurrentConversation,
    setHasNewChat,
    onCreditUpdate,
    tools,
    selectedAgents // Add selectedAgents to the dependency array
  ]);

  const stopConversation = useCallback(async () => {
    if (!user?.address) {
      throw new Error('Not connected to wallet');
    }

    const profile = await storage.getProfile(user.address);
    if (!profile?.jwt || !profile?.did) {
      throw new Error('Profile not found or invalid');
    }

    await stopStream(
      {
        jwt: profile.jwt,
        did: profile.did,
        walletAddress: user.address,
        nonce: profile.nonce
      },
      (message) => setMessages(prev => [...prev, message])
    );
    setHasNewChat(false);
  }, [user, stopStream, setHasNewChat]);

  const regenerateMessage = useCallback(async (index: number) => {
    // Find the last user message
    // const lastUserMessageIndex = [...messages].reverse().findIndex(m => m.sender === 'user');
    if (!messages[index - 1]) {
      notify.error('No message to regenerate');
      return;
    }

    // const lastUserMessage = messages[messages.length - 1 - lastUserMessageIndex];
    const lastUserMessage = messages[index - 1];
    if (lastUserMessage && lastUserMessage.content) {
      await sendMessage(lastUserMessage.content, tools);
    } else {
      notify.error('Cannot regenerate message with empty content');
    }
  }, [messages, sendMessage, tools, selectedAgents]);

  return {
    conversations,
    currentConversation,
    setCurrentConversation: selectConversation,
    messages,
    loading,
    sending,
    createConversation: handleNewChat,
    sendMessage,
    stopConversation,
    regenerateMessage,
    hasNewChat,
    tools,
    setTools,
    deleteConversation,
    togglePin,
    syncConversations
  };
};
